import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { instance } from '../axios-api/axios-api';
import { HashLoader } from 'react-spinners';
import { Buffer } from 'buffer';
import { Helmet } from 'react-helmet';

const ICON_FIELDS = [
  { name: 'whatsapp', label: 'Whatsapp', icon: '24whatsapp.svg', href: v => `https://wa.me/${v}` },
  { name: 'whatsappBusiness', label: 'WA Business', icon: '24whatsappb.svg', href: v => `https://wa.me/${v}` },
  { name: 'googleReview', label: 'G Review', icon: 'reviews.svg', href: v => v },
  { name: 'phone', label: 'Phone', icon: '2call.svg', href: v => `tel:${v}` },
  { name: 'mail', label: 'Mail', icon: 'mail.svg', href: v => `mailto:${v}` },
  { name: 'linkedin', label: 'Linkedin', icon: '20linkedin.svg', href: v => `https://www.linkedin.com/in/${v}` },
  { name: 'twitter', label: 'Twitter', icon: '13twitter.svg', href: v => `https://twitter.com/${v}` },
  { name: 'facebook', label: 'Facebook', icon: '8fb.svg', href: v => `https://www.facebook.com/${v}`, imgClassName: 'h-full' },
  { name: 'website', label: 'Website', icon: 'website.svg', href: v => v },
  { name: 'youtube', label: 'Youtube', icon: '1youtube.svg', href: v => v, imgClassName: 'h-full' },
  { name: 'spotify', label: 'Spotify', icon: '5spotify.svg', href: v => `https://open.spotify.com/track/${v}`, imgClassName: 'h-full' },
  { name: 'telegram', label: 'Telegram', icon: 'telegram.svg', href: v => `https://t.me/${v}` },
  { name: 'instagram', label: 'Instagram', icon: '11instagram.svg', href: v => `https://www.instagram.com/${v}` },
  { name: 'skype', label: 'Skype', icon: '22skype.svg', href: v => `skype:${v}` },
  { name: 'location', label: 'Location', icon: 'location.svg', href: v => `https://www.google.com/maps?q=${v}` },
  { name: 'threads', label: 'Threads', icon: 'threads.svg', href: v => `https://www.threads.net/${v}` },
  { name: 'pinterest', label: 'Pinterest', icon: '6pinterest.svg', href: v => `https://www.pinterest.com/${v}`, imgClassName: 'fb h-full' },
  { name: 'dribble', label: 'Dribble', icon: '3dribbble.svg', href: v => `https://dribbble.com/${v}`, imgClassName: 'h-full' },
  { name: 'vimeo', label: 'Vimeo', icon: '4vimeo.svg', href: v => `https://vimeo.com/${v}`, imgClassName: 'h-full' },
  { name: 'rss', label: 'RSS', icon: '15rss.svg', href: v => v, imgClassName: 'h-full', noActiveClass: true },
  { name: 'tiktok', label: 'Tiktok', icon: '9tiktok.svg', href: v => v},
  { name: 'soundcloud', label: 'Soundcloud', icon: '9soundcloud.svg', href: v => v, noActiveClass: true },
  { name: 'warwick', label: 'Warwick', icon: '10warwick.svg', href: v => v, noActiveClass: true },
  { name: 'paypal', label: 'Paypal', icon: '23paypal.svg', href: v => `https://www.paypal.com/paypalme/${v}`, noActiveClass: true },
  { name: 'dropbox', label: 'Dropbox', icon: '21dropbox.svg', href: v => v },
  { name: 'cute', label: 'Cute', icon: '12cute.svg', href: v => v, noActiveClass: true },
  { name: 'snapchat', label: 'Snapchat', icon: '14snapchat.svg', href: v => `https://www.snapchat.com/add/${v}` },
  { name: 'tumblr', label: 'Tumblr', icon: '18tumblr.svg', href: v => `https://${v}.tumblr.com`, imgClassName: 'fb', noActiveClass: true },
  { name: 'behance', label: 'Behance', icon: '19behance.svg', href: v => `https://www.behance.net/${v}` },
  { name: 'flickr', label: 'Flickr', icon: '25flickr.svg', href: v => v },
];

function ContactIcon({ field, entry }) {
  const isActive = !field.noActiveClass && entry && entry.active === true;
  return (
    <li className={isActive ? 'active' : ''}>
      <a
        href={entry ? field.href(entry.value) : '#'}
        target="_blank"
        rel="noreferrer"
      >
        <img
          src={`../assets/images/icons/${field.icon}`}
          alt=""
          className={field.imgClassName || ''}
        />
        <div className="name">{field.label}</div>
      </a>
    </li>
  );
}

export function UserDetails() {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageBase64String, setImageBase64String] = useState(null);
  const [userDataFiltered, setData] = useState({});
  const [userDetails, setUserDetails] = useState({
    designation: '',
    company: '',
    username: '',
  });
  const [userContact, setUserContact] = useState({
    name: '',
    phone: '',
    email: '',
  });

  useEffect(() => {
    setIsLoading(true);
    instance
      .get(`/user/get-user/${id}`)
      .then(response => {
        setIsLoading(false);
        const dataArray = response.data.dataArray
          ? response.data.dataArray
          : [];
        const userDetails = response.data.userDetails
          ? response.data.userDetails
          : [];

        let imageArray = response.data ? response.data.image : {};

        if (imageArray && imageArray.id) {
          const imageUn = new Uint8Array(imageArray.data.data);
          setImageBase64String(imageUn);
          const blob = new Blob([imageUn.buffer], { type: 'image/jpeg' });
          const imageUrl = URL.createObjectURL(blob);
          setImageUrl(imageUrl);
        }

        setUserDetails({
          company: userDetails?.company ? userDetails?.company : '',
          username: userDetails?.username ? userDetails?.username : '',
          designation: userDetails?.designation ? userDetails?.designation : '',
        });
        if (dataArray && dataArray.length > 0) {
          const obj = dataArray.reduce((acc, curr) => {
            acc[curr.name] = curr;
            return acc;
          }, {});

          setData(obj);

          setUserContact({
            youtube: obj.youtube ? obj.youtube.value : null,
            instagram: obj.instagram ? obj.instagram.value : null,
            linkedin: obj.linkedin ? obj.linkedin.value : null,
            email: obj.mail ? obj.mail.value : null,
            phone: obj.phone ? obj.phone.value : null,
            name: userDetails ? userDetails.username : null,
            id: id,
          });
        }
      })
      .catch(error => {
        setIsLoading(false);
        const err = error?.response?.data?.message
          ? error?.response.data.message
          : error?.message;
        toast.error(err);
      });
  }, [id]);

  function capitalizeFirstLetter(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  const handleSave = async () => {
    let base64Image;
    if (imageBase64String) {
      const buffer = Buffer.from(imageBase64String);
      base64Image = buffer.toString('base64');
    } else {
      base64Image = '';
    }

    var contact = userContact;

    var vcard =
      'BEGIN:VCARD\n' +
      'VERSION:3.0\n' +
      'FN:' +
      contact.name +
      '\n' +
      'TEL;TYPE=work,voice:' +
      contact.phone +
      '\n' +
      'EMAIL:' +
      contact.email +
      '\n' +
      'PHOTO;TYPE=JPEG;ENCODING=B:' +
      base64Image +
      '\n' +
      'URL;TYPE=LINKEDIN:https://www.linkedin.com/in/' +
      contact.linkedin +
      '\n' +
      'URL;TYPE=WHATSAPP:https://wa.me/' +
      contact.phone +
      '\n' +
      'URL;TYPE=INSTAGRAM:https://www.instagram.com/' +
      contact.instagram +
      '\n' +
      'URL;TYPE=YOUTUBE:' +
      contact.youtube +
      '\n' +
      'URL;TYPE=DIGIC:https://www.digiccard.com/' +
      id +
      '\n' +
      'END:VCARD';

    var blob = new Blob([vcard], { type: 'text/vcard' });
    var url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.download = `${contact.name}.vcf`;
    link.href = url;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="allstyle">
      {isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <HashLoader color="#b8846d" />
        </div>
      ) : (
        <div>
          {userDataFiltered && Object.keys(userDataFiltered).length > 0 ? (
            <div>
              <Helmet>
                <meta
                  property="og:title"
                  content={capitalizeFirstLetter(userDetails.username)}
                />
                <meta property="og:image" content={imageUrl} />
                <meta
                  property="og:url"
                  content={'https://www.digiccard.com/' + id}
                />
              </Helmet>
              <section>
                <div className="profile_con mt-5">
                  <span className="profile">
                    <span className="pic">
                      <img
                        src={imageUrl ? imageUrl : '../assets/images/user.png'}
                        alt=""
                        className="h-full img-fluid"
                      />
                    </span>
                  </span>
                  <h1 className="mb-2">
                    {capitalizeFirstLetter(userDetails.username)}
                  </h1>
                  <h5>
                    <span className="text-gray fw-lightery">
                      {' '}
                      {capitalizeFirstLetter(userDetails.designation)}
                    </span>
                    <span className="d-block fw-bold mt-2">
                      {capitalizeFirstLetter(userDetails.company)}
                    </span>
                  </h5>
                  <div className="divider divider-width">
                    <span></span>
                  </div>
                  <h4 className="mt-5">Click Below Icons to Contact</h4>
                </div>
                <div className="icons icon-width pt-0">
                  <ul id="myList">
                    {ICON_FIELDS.map(field => (
                      <ContactIcon
                        key={field.name}
                        field={field}
                        entry={userDataFiltered[field.name]}
                      />
                    ))}
                  </ul>
                </div>
                <ul className="btm_btn exchange-btn">
                  <li className="w-100">
                    <button onClick={handleSave}>SAVE CONTACT</button>
                  </li>
                </ul>
              </section>
            </div>
          ) : (
            <div>
              <h1>Hello! Welcome To Digic,The Given User Was Not Found</h1>
            </div>
          )}
          <div>
            <ToastContainer theme="dark" />
          </div>
        </div>
      )}
    </div>
  );
}
